// Lookups and predicates over DB/PROGRAMS/session used across every domain.
// ageAsOfSept1/seasonLabel come from /js/shared/age-season.js (a classic
// script loaded before this module, so they're already on window/global scope).

import { TODAY } from './format.js';
import { DB, PROGRAMS, session } from './state.js';

// ── SEASON HELPERS ────────────────────────────────────────────────────
// A season is identified by its start year. 2026 = the 2026–27 season.
// The upcoming season is what families register FOR.
export function upcomingSeasonYear() {
  // Before Sept 1 → upcoming season starts this calendar year; on/after → next year.
  const y = TODAY.getFullYear();
  return (TODAY.getMonth() + 1) >= 9 ? y + 1 : y;
}
export function currentSeasonYear() { return upcomingSeasonYear() - 1; }

// Which programs is a performer eligible for in the upcoming season,
// given their Sept-1 age and their completed-season history?
export function eligiblePrograms(performer, seasonStartYear) {
  const age = ageAsOfSept1(performer.dob, seasonStartYear);
  if (age === null) return [];
  return PROGRAMS.filter(p => {
    if (p.billingType === 'workshop') return false; // workshops register through their own flow, not season re-enrollment
    if (!p.active && p.active !== undefined) return false;
    const min = p.ageMin ?? 0;
    const max = p.ageMax ?? 99;
    return age >= min && age <= max;
  });
}
// Is this performer in PC/BB this season, making them eligible for priority
// re-enrollment? A class or workshop doesn't count (decided 2026-10-13);
// ReenrollRoutes enforces the same rule.
export function isContinuingPerformer(performerId) {
  return DB.seasonEnrollments.some(se =>
    se.performerId === performerId && se.seasonYear === currentSeasonYear() && se.status === 'registered'
    && PROGRAMS.some(p => p.id === se.programId && p.isProgram));
}
// What program did they complete this season (for carry-forward logic)?
export function currentSeasonProgram(performerId) {
  const se = DB.seasonEnrollments.find(s =>
    s.performerId === performerId && s.seasonYear === currentSeasonYear() && s.status === 'completed');
  return se ? se.programId : null;
}
// The current enrollment for a performer in a given program (registered or active).
// Deliberately unfiltered by season: /enrollments returns every season ordered
// season_year DESC, so the first match is the newest one. That ordering is what
// makes enrollmentBillingMode() and enrollmentMonthlyRate() answer about this
// season rather than a stale row from a finished one.
export function activeEnrollment(performerId, programId) {
  return DB.seasonEnrollments.find(s =>
    s.performerId === performerId && s.programId === programId && (s.status === 'registered' || s.status === 'active')) || null;
}
// A performer's current billing mode for a program (defaults to monthly).
export function enrollmentBillingMode(performerId, programId) {
  const se = activeEnrollment(performerId, programId);
  return se ? (se.billingMode || 'monthly') : 'monthly';
}
// The monthly tuition actually billed for one performer in one program.
//
// Almost always the program's own rate. A handful of enrollments carry a
// rateOverride -- a reduced rate Lori agreed to in person (a volunteer-hours
// trade, most often), set by scripts/rate_override.php and stored on the
// enrollment rather than the program so it never reaches the public catalog.
// For those, the program's number is wrong everywhere it is shown: the billing
// crons charge this one, so anything the family reads has to match it.
//
// Program-level prices (the catalog, Settings, the enroll picker) correctly go
// on using priceLabel(program) -- those describe what the program costs, not
// what one family pays.
export function enrollmentMonthlyRate(performerId, programId) {
  const se = activeEnrollment(performerId, programId);
  if (se && se.rateOverride != null) return se.rateOverride;
  const p = PROGRAMS.find(x => x.id === programId);
  return p ? (p.monthly || 0) : 0;
}
// True when this performer pays something other than the program's rate --
// the cue for the UI to say so rather than quietly showing a different number.
export function hasRateOverride(performerId, programId) {
  const se = activeEnrollment(performerId, programId);
  return !!(se && se.rateOverride != null);
}
// ── REGISTRATION WINDOW STATUS ────────────────────────────────────────
export function priorityWindowOpen() {
  const s = DB.settings;
  if (s.priorityManualClosed) return false;
  // Same rule as RegistrationWindow::isOpen('returning') on the server.
  return TODAY >= new Date(s.priorityOpensAt) && (!s.priorityClosesAt || TODAY <= new Date(s.priorityClosesAt));
}
// "Fri, Aug 14, 9:00 PM" for the re-enrollment deadline, or null when none is set.
export function priorityDeadlineLabel() {
  const c = DB.settings.priorityClosesAt;
  return c ? new Date(c).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;
}
export function openEnrollmentOpen() {
  const s = DB.settings;
  if (s.openEnrollmentManualClosed || s.stopAcceptingNew) return false;
  // Same rule as RegistrationWindow::isOpen('new') on the server.
  return TODAY >= new Date(s.openEnrollmentOpensAt) && (!s.registrationClosesAt || TODAY <= new Date(s.registrationClosesAt));
}

export const programName = id => (PROGRAMS.find(p => p.id === id) || {}).name || '—';
export const kidPrograms = k => k.programIds || (k.programId ? [k.programId] : []);
export const kidInProgram = (k, pid) => kidPrograms(k).includes(pid);
export const kidProgramNames = k => kidPrograms(k).map(id => programName(id)).join(', ') || 'No program';
// Workshops are excluded — they register through their own dedicated flow
// (WorkshopRoutes checkout/promote, with capacity+waitlist handling), never
// through the generic admin "enroll in a program" picker or reenroll flow.
export const activePrograms = () => PROGRAMS.filter(p => p.active !== false && p.billingType !== 'workshop');
export const childName = id => (DB.children.find(c => c.id === id) || {}).name || '—';
export const acctName = id => { const u = DB.users.find(u => u.id === id); return u ? u.firstName + ' ' + u.lastName : '—'; };
export const acctEmail = id => { const u = DB.users.find(u => u.id === id); return u ? u.email : ''; };
export const acctPhone = id => { const u = DB.users.find(u => u.id === id); return u ? (u.phone || '') : ''; };
// Destination address for an email log entry: account's email when linked, else the stored toEmail
export const emailTo = e => (e.accountId ? acctEmail(e.accountId) : '') || e.toEmail || '—';
export const isValidEmail = e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

// role helpers
export const hasRole = (u, r) => (u.roles || [u.role]).includes(r);
export const isAdmin = u => hasRole(u, 'admin');
export const isParent = u => hasRole(u, 'parent');
export const isDual = u => isAdmin(u) && isParent(u);
export const canEdit = u => isAdmin(u) && u.adminRole !== 'view_only';
// Archived = filed away but fully intact, ready to come back. Everything the
// office works from filters these out; the Archived views are how you find them
// again. Never destructive — remove_family.php is still the only hard delete.
export const isArchived = r => !!(r && r.archivedAt);
// Loose UX gate for showing the Store nav tab — real per-sale eligibility is
// re-checked server-side against that sale's own seasonYear (StoreRoutes).
export const isPcFamily = () => {
  if (!session) return false;
  const pc = PROGRAMS.find(p => p.name === 'Performance Company' && p.isProgram);
  if (!pc) return false;
  const myIds = DB.children.filter(c => c.accountId === session.id).map(c => c.id);
  return DB.seasonEnrollments.some(se => se.programId === pc.id && se.status === 'registered' && myIds.includes(se.performerId));
};

// ── ADDRESS HELPERS ───────────────────────────────────────────────────
// One definition of "we have an address" shared by the parent card, the
// admin parent record, and the missing-info login prompt, so the prompt
// can never disagree with what the cards render. State is deliberately not
// required: it defaults to UT on the registration form and is the one part
// staff can reliably infer.
export const hasAddress = u => !!(u && (u.address || '').trim() && (u.city || '').trim() && (u.zip || '').trim());
export const formatAddress = u => {
  if (!u) return '';
  const street = (u.address || '').trim();
  const cityLine = [(u.city || '').trim(), [(u.state || '').trim(), (u.zip || '').trim()].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  return [street, cityLine].filter(Boolean).join('\n');
};
